import React, { useState, useMemo } from 'react';
import api from '../../services/api';
import FormField from './FormField';
import { shouldShowQuestion } from '../../utils/conditionalLogic';
import './DelegateInputPanel.css';

/**
 * Panel shown to delegates when a request is in 'Needs Input' status.
 * Displays only the fields assigned to the current user and allows submission.
 */
const DelegateInputPanel = ({ requestId, delegationRound, questions, currentAnswers, currentUserEmail, onSubmitted, onSwitchToDelegation }) => {
  const [localAnswers, setLocalAnswers] = useState({});
  const [submittedNotes, setSubmittedNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Find assignments belonging to the current user
  const myAssignments = useMemo(() => {
    if (!delegationRound?.assignments || !currentUserEmail) return [];
    return delegationRound.assignments.filter(
      a => a.status === 'pending' && a.assigned_to_email.toLowerCase() === currentUserEmail.toLowerCase()
    );
  }, [delegationRound, currentUserEmail]);

  // Build a lookup: questionKey → question schema entry
  const questionMap = useMemo(() => {
    const m = {};
    (questions || []).forEach(q => { m[q.question_key] = q; });
    return m;
  }, [questions]);

  // Combined answers (existing + local edits) for conditional logic evaluation
  const mergedAnswers = useMemo(() => {
    const base = {};
    (currentAnswers || []).forEach(a => { base[a.question_key] = a.answer_value; });
    return { ...base, ...localAnswers };
  }, [currentAnswers, localAnswers]);

  const handleFieldChange = (key, value) => {
    setLocalAnswers(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const assignmentIds = myAssignments.map(a => a.id);
      await api.submitDelegationAssignments(requestId, {
        assignmentIds,
        fieldAnswers: localAnswers,
        submittedNotes: submittedNotes.trim() || undefined,
      });
      setSuccess(true);
      onSubmitted();
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!delegationRound) return null;

  // No assignment for this user
  if (myAssignments.length === 0) {
    return (
      <div className="dip__panel dip__panel--info">
        <div className="dip__info-icon">ℹ️</div>
        <div>
          <p className="dip__info-title">Input requested from other team members</p>
          <p className="dip__info-body">
            This request is waiting for input from assigned delegates.
            You are not assigned to provide input on this request.
            {onSwitchToDelegation && (
              <> See the{' '}
                <button
                  onClick={onSwitchToDelegation}
                  style={{ background: 'none', border: 'none', padding: 0, color: '#0066cc', cursor: 'pointer', fontSize: 'inherit', textDecoration: 'underline' }}
                >
                  Delegation tab
                </button>
                {' '}for the full status of who has been asked and what fields are requested.
              </>
            )}
          </p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="dip__panel dip__panel--success">
        <div className="dip__info-icon">✅</div>
        <div>
          <p className="dip__info-title">Input submitted</p>
          <p className="dip__info-body">Your answers have been saved. The approver will be notified once all delegates have responded.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dip__panel">
      <div className="dip__header">
        <h3 className="dip__title">Your Assigned Fields</h3>
        {delegationRound.general_notes && (
          <div className="dip__general-note">
            <strong>Note from approver:</strong> {delegationRound.general_notes}
          </div>
        )}
      </div>

      {myAssignments.map(assignment => {
        // Gather questions for this group that are conditionally visible
        const visibleQuestions = (assignment.field_keys || [])
          .map(key => questionMap[key])
          .filter(q => q && shouldShowQuestion(q, mergedAnswers));

        return (
          <div key={assignment.id} className="dip__group">
            <h4 className="dip__group-label">{assignment.group_label}</h4>
            {assignment.approver_note && (
              <p className="dip__approver-note">{assignment.approver_note}</p>
            )}

            {visibleQuestions.length === 0 ? (
              <p className="dip__no-fields">No fields to fill in for this group based on your current answers.</p>
            ) : (
              <div className="dip__fields">
                {visibleQuestions.map(q => {
                  const value = mergedAnswers[q.question_key] ?? '';
                  const options = q.dropdown_options
                    ? q.dropdown_options.map(o => (typeof o === 'string' ? o : (o.value || String(o))))
                    : [];
                  return (
                    <FormField
                      key={q.question_key}
                      label={q.question_text}
                      type={q.field_type || 'text'}
                      value={value}
                      onChange={val => handleFieldChange(q.question_key, val)}
                      required={q.is_required}
                      options={options}
                      helpText={q.help_text}
                    />
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      <div className="dip__footer">
        <div className="dip__notes-row">
          <label className="dip__notes-label">Add a note (optional)</label>
          <textarea
            className="dip__notes-textarea"
            rows={2}
            value={submittedNotes}
            onChange={e => setSubmittedNotes(e.target.value)}
            placeholder="Any context or caveats you want to share with the approver…"
          />
        </div>
        {error && <p className="dip__error">{error}</p>}
        <button
          className="dip__submit-btn"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? 'Submitting…' : 'Submit Input'}
        </button>
      </div>
    </div>
  );
};

export default DelegateInputPanel;
